/**
 * utils/betaApplicationConverters.ts
 * Utility functions for converting between beta application data formats
 *
 * Handles conversion from draft (localStorage) format to persistent (Supabase) format
 * and validation of application data before submission.
 */

import type { BetaApplicationDraft } from '@/components/BetaApplication/types/BetaApplicationDraft';
import type { BetaApplicationInsert } from '@/types/supabase/BetaApplication';
import type {
  ExperienceLevel,
  TradingStyle,
  BetaAccountType,
  TestingCommitment,
  FeedbackCommitment,
  BetaApplicationStep
} from '@/components/BetaApplication/types/BetaApplicationTypes';
import { Brand } from '@enfomentis/shared/config';

/**
 * Convert BetaApplicationDraft to BetaApplicationInsert
 *
 * Transforms the draft format (with nested objects and empty strings)
 * to the flat format required for Supabase insertion.
 *
 * @param draft - Beta application draft from form wizard
 * @returns Formatted data ready for Supabase insertion
 */
export function convertDraftToApplication(draft: BetaApplicationDraft): BetaApplicationInsert {
  return {
    // Account Information
    email: draft.account.email,
    fullName: draft.account.fullName,
    discordUsername: draft.account.discordUsername,

    // Trading Profile
    primaryFocus: draft.trading.primaryFocus,
    experienceLevel: draft.trading.experienceLevel as ExperienceLevel,
    tradingStyle: draft.trading.tradingStyle as TradingStyle,
    tradingStyleOther: draft.trading.tradingStyleOther || null,
    accountType: draft.trading.accountType as BetaAccountType,
    propFirm: draft.trading.propFirm || '',
    propFirmStatus: draft.trading.propFirmStatus || '',
    propFirmPayoutHistory: draft.trading.propFirmPayoutHistory || '',
    broker: draft.trading.broker,
    tradingSessions: draft.trading.tradingSessions,
    tradingSessionsOther: draft.trading.tradingSessionsOther || null,
    currentJournaling: draft.trading.currentJournaling,

    // Beta Commitment
    stjInterest: draft.commitment.stjInterest,
    testingCommitment: draft.commitment.testingCommitment as TestingCommitment,
    feedbackCommitment: draft.commitment.feedbackCommitment as FeedbackCommitment,

    // User relationship (null until approved)
    userId: null,

    // Admin fields (set defaults for new applications)
    status: 'pending',
    reviewedBy: null,
    reviewedAt: null,
    adminNotes: null,

    // Email queue status (for confirmation email)
    emailStatus: 'pending'
  };
}

/**
 * Validate draft application before submission
 *
 * Checks that all required fields are filled and formats are valid.
 * Returns array of validation errors, empty if valid.
 *
 * @param draft - Beta application draft to validate
 * @returns Array of validation error messages
 */
export function validateDraftApplication(draft: BetaApplicationDraft): string[] {
  const errors: string[] = [];

  // Account Information Validation
  if (!draft.account.email?.trim()) {
    errors.push('Email is required');
  } else if (!isValidEmail(draft.account.email)) {
    errors.push('Please enter a valid email address');
  }

  if (!draft.account.fullName?.trim()) {
    errors.push('Full name is required');
  }

  if (!draft.account.discordUsername?.trim()) {
    errors.push('Discord username is required');
  }

  // Trading Profile Validation
  if (!draft.trading.primaryFocus?.length) {
    errors.push('Please select at least one trading focus');
  }

  if (!draft.trading.experienceLevel) {
    errors.push('Trading experience level is required');
  }

  if (!draft.trading.tradingStyle) {
    errors.push('Trading style is required');
  }

  if (draft.trading.tradingStyle === 'other' && !draft.trading.tradingStyleOther?.trim()) {
    errors.push('Please specify your trading style');
  }

  if (!draft.trading.accountType) {
    errors.push('Account type is required');
  }

  if (!draft.trading.broker?.trim()) {
    errors.push('Broker is required');
  }

  if (!draft.trading.tradingSessions?.length) {
    errors.push('Please select at least one trading session');
  }

  if (draft.trading.tradingSessions?.includes('other') && !draft.trading.tradingSessionsOther?.trim()) {
    errors.push('Please specify your trading sessions');
  }

  if (!draft.trading.currentJournaling?.trim()) {
    errors.push('Current journaling method is required');
  }

  // Beta Commitment Validation
  if (!draft.commitment.stjInterest?.trim()) {
    errors.push(`Please describe your interest in ${Brand.displayName} beta testing`);
  }

  if (!draft.commitment.testingCommitment) {
    errors.push('Testing commitment level is required');
  }

  if (!draft.commitment.feedbackCommitment) {
    errors.push('Feedback commitment level is required');
  }

  return errors;
}

/**
 * Check if all required steps are completed
 *
 * @param draft - Beta application draft to check
 * @returns True if all steps are completed
 */
export function isDraftComplete(draft: BetaApplicationDraft): boolean {
  const requiredSteps: BetaApplicationStep[] = ['account', 'trading', 'style-experience', 'commitment'];
  return requiredSteps.every(step =>
    draft.completedSteps.includes(step)
  );
}

/**
 * Simple email validation
 *
 * @param email - Email address to validate
 * @returns True if email format is valid
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Generate application summary for confirmation
 *
 * @param draft - Beta application draft
 * @returns Human-readable summary of application
 */
export function generateApplicationSummary(draft: BetaApplicationDraft): string {
  const focus = draft.trading.primaryFocus.join(', ');
  const sessions = draft.trading.tradingSessions.join(', ');

  return `
Application Summary:
• Name: ${draft.account.fullName}
• Email: ${draft.account.email}
• Discord: ${draft.account.discordUsername}
• Trading Focus: ${focus}
• Experience: ${draft.trading.experienceLevel}
• Style: ${draft.trading.tradingStyle}
• Broker: ${draft.trading.broker}
• Sessions: ${sessions}
• Commitment: ${draft.commitment.testingCommitment}
  `.trim();
}